/**
 * PUAコマンド定義（表示層: PUAマーカー ⇔ CSSクラスの対応表）
 *
 * エージェント向け規約:
 * - 「PUAコマンド」（このパッケージ: 表示装飾のマーカー定義）と「BBSコマンド」
 *   （bbs-command: 投稿本文の機能記法）は別概念。混同しないこと
 * - 装飾の追加・変更はこのファイルだけを編集する。tokenize/validate/strip 本体は触らない
 * - idChar は一度割り当てたら変更・再利用禁止（DB保存済み投稿が壊れる）。
 *   装飾を廃止してもIDは欠番として残す
 * - 逆引きテーブルは定義から自動導出する。手書き禁止
 * - 検証は validatePuaDefinitions がモジュール読み込み時に実行し、違反は throw する。
 *   同内容のユニットテストを対で更新すること
 * - className はフロントの CSS クラス。CSS 側（同ディレクトリの pua.css）が定義に追随する
 * - 対話（クリック等）は interaction に宣言する。消費側アプリは kind ごとの汎用実装を
 *   1つずつ持つだけで、どの装飾かは知らない。既存 kind を選ぶ限りアプリ側の編集は不要
 */

import { PAIRED_ID_RANGE } from "./markers"

export type PuaStyleKey = "dice_result" | "hide"

/**
 * 装飾ランの対話仕様。
 *
 * 新しい kind を足すときだけ消費側アプリの汎用実装に手が入る。
 * 装飾を増やすだけなら既存 kind を選べばよい。
 */
export type PuaInteraction =
  /** 表示のみ。イベントを持たない */
  | { readonly kind: "static" }
  /**
   * クリック/キーボードで2状態を切り替える。
   * 状態は aria-pressed に載り、見た目の差は pua.css の属性セレクタが担当する。
   */
  | {
      readonly kind: "toggle"
      /** 初期の押下状態 */
      readonly initialPressed: boolean
      /** ポインタ操作のヒント（title属性）。本文をアクセシブルネームに残すため aria-label は使わない */
      readonly hint: string
    }

/**
 * 対話を持つ装飾の仕様（static を除いたもの）。kind が増えても自動で追随する。
 * 消費側アプリはこの型を kind で網羅分岐し、未対応の kind をコンパイルエラーで検出する。
 */
export type PuaActiveInteraction = Exclude<PuaInteraction, { readonly kind: "static" }>

/** 対話を持つラン全てに付く汎用クラス。装飾固有ではないため定義の className には使えない */
export const INTERACTIVE_CLASS_NAME = "pua-interactive"

export interface PuaStyleDefinition {
  readonly key: PuaStyleKey
  /** マーカー直後に置くID文字。PAIRED_ID_RANGE 内の1文字。恒久割り当て */
  readonly idChar: string
  /** フロントが装飾に使うCSSクラス名 */
  readonly className: string
  readonly description: string
  /** 対話仕様。表示だけの装飾は { kind: "static" } */
  readonly interaction: PuaInteraction
}

export const PUA_STYLE_DEFINITIONS: readonly PuaStyleDefinition[] = [
  {
    key: "dice_result",
    idChar: "\uE100",
    className: "pua-dice",
    description: "ダイス結果の強調表示",
    interaction: { kind: "static" },
  },
  {
    key: "hide",
    idChar: "\uE101",
    className: "pua-hide",
    description: "伏せ字（本文色×本文色。ドラッグ選択で読める）",
    // Safariは選択時に文字色を反転しないため、選択に依存しない読解手段としてクリックでも反転できる
    interaction: {
      kind: "toggle",
      initialPressed: false,
      hint: "クリックで伏せ字の表示/非表示を切り替え",
    },
  },
]

const CLASS_NAME_PATTERN = /^pua-[a-z][a-z0-9-]*$/

/**
 * 定義の整合性を検証し、違反があれば throw する。
 * モジュール読み込み時（＝プロセス起動時）に自己実行され、不正定義の混入を即死で検出する。
 */
export function validatePuaDefinitions(defs: readonly PuaStyleDefinition[]): void {
  const seenKeys = new Set<string>()
  const seenIds = new Set<string>()
  const seenClassNames = new Set<string>()
  for (const def of defs) {
    if (seenKeys.has(def.key)) {
      throw new Error(`PUAコマンドのkeyが重複しています: ${def.key}`)
    }
    seenKeys.add(def.key)
    const codePoints = [...def.idChar]
    if (codePoints.length !== 1) {
      throw new Error(`PUAコマンド ${def.key} のidCharは1文字でなければなりません`)
    }
    const code = def.idChar.codePointAt(0) ?? 0
    if (code < PAIRED_ID_RANGE.min || code > PAIRED_ID_RANGE.max) {
      throw new Error(
        `PUAコマンド ${def.key} のidChar (U+${code.toString(16).toUpperCase()}) が開閉ペア型のID範囲外です`,
      )
    }
    if (seenIds.has(def.idChar)) {
      throw new Error(`PUAコマンド ${def.key} のidCharが他の定義と重複しています`)
    }
    seenIds.add(def.idChar)
    if (!CLASS_NAME_PATTERN.test(def.className)) {
      throw new Error(`PUAコマンド ${def.key} のclassNameは ${CLASS_NAME_PATTERN} に一致する必要があります`)
    }
    if (def.className === INTERACTIVE_CLASS_NAME) {
      throw new Error(`PUAコマンド ${def.key} のclassNameは予約名 ${INTERACTIVE_CLASS_NAME} と衝突しています`)
    }
    if (seenClassNames.has(def.className)) {
      throw new Error(`PUAコマンド ${def.key} のclassNameが他の定義と重複しています`)
    }
    seenClassNames.add(def.className)
    if (def.interaction.kind === "toggle" && def.interaction.hint === "") {
      throw new Error(`PUAコマンド ${def.key} のinteraction.hintは空にできません`)
    }
  }
}

validatePuaDefinitions(PUA_STYLE_DEFINITIONS)

/** 逆引き（ID文字→定義）。定義から自動導出。手書き禁止 */
export const DEFINITION_BY_ID: ReadonlyMap<string, PuaStyleDefinition> = new Map(
  PUA_STYLE_DEFINITIONS.map((def) => [def.idChar, def]),
)

/** 逆引き（key→定義）。定義から自動導出。手書き禁止 */
export const DEFINITION_BY_KEY: ReadonlyMap<PuaStyleKey, PuaStyleDefinition> = new Map(
  PUA_STYLE_DEFINITIONS.map((def) => [def.key, def]),
)

/** 逆引き（CSSクラス名→定義）。定義から自動導出。手書き禁止。attributes.ts の内部利用のみ */
export const DEFINITION_BY_CLASS_NAME: ReadonlyMap<string, PuaStyleDefinition> = new Map(
  PUA_STYLE_DEFINITIONS.map((def) => [def.className, def]),
)
